

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export type Theme = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

export function getThemeFromCookies(cookieHeader: string | null): Theme | null {
  if (!cookieHeader) return null;
  const match = cookieHeader.match(/(^|; )theme=([^;]+)/);
  const value = match?.[2];
  if (value === "dark" || value === "light" || value === "system") {
    return value;
  }
  return null;
}

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_COOKIE_NAME = "theme";
const THEME_ATTRIBUTE = "class";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredTheme(): Theme | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(^| )${THEME_COOKIE_NAME}=([^;]+)`),
  );
  const value = match?.[2];
  if (value === "dark" || value === "light" || value === "system") {
    return value;
  }
  return null;
}

function setStoredTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  // Set cookie with 1 year expiry
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${THEME_COOKIE_NAME}=${theme};path=/;max-age=${maxAge};SameSite=Lax`;
}

function applyTheme(resolvedTheme: ResolvedTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolvedTheme);
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  /** Initial theme from server (from cookie) */
  initialTheme?: Theme | null;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  initialTheme,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(
    () => initialTheme ?? getStoredTheme() ?? defaultTheme,
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const currentTheme = initialTheme ?? getStoredTheme() ?? defaultTheme;
    if (currentTheme === "system") {
      return getSystemTheme();
    }
    return currentTheme;
  });

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);

    const resolved = newTheme === "system" ? getSystemTheme() : newTheme;
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  // Apply theme on mount and handle system theme changes
  useEffect(() => {
    const resolved = theme === "system" ? getSystemTheme() : theme;
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        const newResolved = getSystemTheme();
        setResolvedTheme(newResolved);
        applyTheme(newResolved);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/**
 * Script to prevent flash of wrong theme on initial load.
 * Include this in the <head> before any content renders.
 */
export function ThemeScript({
  defaultTheme = "system",
}: {
  defaultTheme?: Theme;
}) {
  const script = `
    (function() {
      const storedTheme = document.cookie.match(/(^| )theme=([^;]+)/)?.[2];
      const theme = storedTheme || "${defaultTheme}";
      let resolved = theme;
      if (theme === "system") {
        resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      }
      document.documentElement.classList.add(resolved);
    })();
  `;

  return <script dangerouslySetInnerHTML={{ __html: script }} />;
}
